import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

import DownArrow from "../../../assets/DownArrow.svg";
import InfoIcon from "../../../assets/InfoIcon.svg";

import { styled } from "@mui/material/styles";

import { RootContext } from "../../../context/RootContext";
import { Roles } from "../../../utils/enums";
import { Clients } from "../../../types/enums";

import { ObjectType } from "../../../types/tableTypes";
import CustomChip from "../CustomChip";
import CustomCard from "../BasicCustomCard";

import CriticalTestIconWithBG from "../../../assets/CriticalFailedWithBG.svg";
import NonCriticalTestIconWithBG from "../../../assets/FailedTestIconBG.svg";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  background: "none",
  border: "none",
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<img src={DownArrow} style={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  borderRadius: "6px",
  border: "1px solid #DBE2FF",
  background: "rgba(228, 234, 255, 0.80)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
   padding: "12px 0px",
  "& MuiPaper-root MuiPaper-elevation MuiPaper-rounded MuiPaper-elevation1 MuiCard-root makeStyles-card-3":
    {
      marginBottom: "12px",
    },
}));

const FAILED_TESTS_PREVIEW_COUNT = 3;

const LoadMoreDivider = ({
  expanded,
  totalCount,
  onClick,
  t
}: {
  expanded: boolean;
  totalCount: number;
  onClick: () => void;
  t: any;
}) => {
  if (expanded) {
    return (
      <div
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        }}
        role="button"
        tabIndex={0}
        data-testid="load-more-tests-button"
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          margin: "12px 0",
        }}
      >
        <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />
        <span
          style={{
            padding: "0 12px",
            color: "#3c3c3c",
            fontWeight: 600,
            fontSize: "14px",
            whiteSpace: "nowrap",
          }}
        >
          {t("allResultsShown", { count: totalCount })}
        </span>
        <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        margin: "12px 0",
        alignItems: "center",
      }}
    >
      <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />&ensp;
      <Button
        variant="outlined"
        onClick={onClick}
        data-testid="load-more-tests-button"
        sx={{
          borderRadius: "20px",
          textTransform: "none",
          borderColor: "#B8B8C7",
          color: "#3c3c3c",
          fontWeight: 600,
          fontSize: "14px",
          paddingX: 3,
        }}
      >
        {t("loadMoreCount", {
          count: totalCount - FAILED_TESTS_PREVIEW_COUNT,
        })}
      </Button>
      &ensp;
      <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />

    </div>
  );
};

export default function LoanResultTab({
  tabData,
  isExpanded,
  expandLeverageAndTests,
  setIsExpanded,
  loanDetails,
  isLoanDSCR,
  ratesAsOfDate,
}: {
  tabData: any;
  isExpanded: any;
  expandLeverageAndTests: boolean;
  setIsExpanded: any;
  loanDetails: ObjectType;
  isLoanDSCR?: boolean;
  ratesAsOfDate?: string;
}) {
  const { t } = useTranslation();
  const { dynamicConfig, userPermissions } = useContext(RootContext) ?? {};
  const isLoanProcessor = !!userPermissions?.includes(Roles.loanProcessor);
  // BplSizer's LoanPage.tsx (v1) flow doesn't carry the loan_processor/
  // underwriter roles this role check depends on elsewhere — it has its own
  // Keycloak role names instead: ORIGINATOR can request a waiver, and
  // LAT_ADMIN/LAT_MEMBER get underwriter-equivalent access to act on one.
  const isBplSizer = dynamicConfig?.ViteAppKcRealm === Clients.BPL_SIZER;
  const isBplSizerOriginator = !!userPermissions?.includes("ORIGINATOR");
  const isBplSizerLatAdminOrMember =
    !!userPermissions?.includes("LAT_ADMIN") ||
    !!userPermissions?.includes("LAT_MEMBER");
  const canActOnWaiver = isBplSizer
    ? isBplSizerLatAdminOrMember
    : !!userPermissions?.includes(Roles.underwriter); 
  const canRequestWaiverRole = isBplSizer ? isBplSizerOriginator : isLoanProcessor;
  const canRequestWaiver = !!dynamicConfig?.showRequestWaiver && canRequestWaiverRole;
  const canViewWaiver =
    !!dynamicConfig?.showRequestWaiver && (canRequestWaiverRole || canActOnWaiver || !!userPermissions?.includes(Roles.creditCommittee)) ;

  const contentDiv = {
    padding: "12px",
    borderRadius: "6px",
    background: "white",
  };

  const [showAllFailures, setShowAllFailures] = useState<
    Record<string, boolean>
  >({});

  const toggleShowAllFailures = (programKey: string) => {
    setShowAllFailures((prev) => ({
      ...prev,
      [programKey]: !prev[programKey],
    }));
  };

  const [openWaiverKey, setOpenWaiverKey] = useState<string | null>(null);

  const getWaiverKey = (tabKey: string, testTitle: string) =>
    `${tabKey}__${testTitle}`;

  const onRequestWaiverClick = (props: any): void => {
    const key = getWaiverKey(props?.currentTabKey, props?.headingText);
    setOpenWaiverKey((prev) => (prev === key ? null : key));
  };

  const handleWaiverSubmit = (waiverData: any) => {
    console.log("waiverData", waiverData);
    setOpenWaiverKey(null);
  };

  const handleWaiverCancel = () => {
    setOpenWaiverKey(null);
  };

  const gridSize = Math.floor(12 / tabData?.length);
  return (
    <div>
      <Grid
        container
        spacing={expandLeverageAndTests ? 0 : 1}
        style={
          expandLeverageAndTests
            ? { overflowX: "auto", flexWrap: "nowrap" }
            : {}
        }
      >
        {tabData?.map((test: ObjectType) => {
          const currentTabKey = Object.keys(test)[0];
          return !expandLeverageAndTests ? (
            <Grid item xs={12} key={currentTabKey}>
              {isLoanDSCR ? (
                <Accordion
                  expanded={isExpanded[currentTabKey]}
                  onChange={() => {
                    setIsExpanded({
                      ...isExpanded,
                      [currentTabKey]: !isExpanded[currentTabKey],
                    });
                  }}
                  data-testid="accordion-button"
                >
                  <AccordionSummary>
                    <h3>{Object.keys(test)[0]}</h3>
                    <CustomChip
                      size="small"
                      label={
                        <div className="div-space-between">
                          <img
                            src={InfoIcon}
                            alt="info-icon"
                            className="mr-8"
                          />
                          {`Failed - ${test[Object.keys(test)[0]]?.length}`}
                        </div>
                      }
                      variant="outlined"
                      className={
                        test[Object.keys(test)[0]]?.length
                          ? "error-chipData"
                          : "success-chipData"
                      }
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    {(showAllFailures[currentTabKey]
                      ? test[Object.keys(test)[0]]
                      : test[Object.keys(test)[0]]?.slice(
                          0,
                          FAILED_TESTS_PREVIEW_COUNT,
                        )
                    )?.map((testEle: ObjectType) => {
                      return (
                        <CustomCard
                          key={testEle.title}
                          headingText={testEle.title}
                          subHeadingText={testEle.subText}
                          loanDetails={loanDetails}
                          fullView={true}
                          ratesAsOfDate={ratesAsOfDate}
                          gridSpace={testEle?.gridSpace ?? 4}
                          showFailed={true}
                          icon={
                            testEle?.isCriticalTest
                              ? CriticalTestIconWithBG
                              : NonCriticalTestIconWithBG
                          }
                          showRequestWaiver={canViewWaiver}
                          canRequestWaiver={canRequestWaiver}
                          canActOnWaiver={canActOnWaiver}
                          onRequestWaiverClick={onRequestWaiverClick}
                          currentTabResults={tabData}
                          currentTabKey={currentTabKey}
                          testType={(testEle as any)?.testType}
                          testKey={(testEle as any)?.testKey}
                          isWaiverFormOpen={
                            openWaiverKey === getWaiverKey(currentTabKey, testEle.title)
                          }
                          onWaiverSubmit={handleWaiverSubmit}
                          onWaiverCancel={handleWaiverCancel}
                        />
                      );
                    })}
                    {test[Object.keys(test)[0]]?.length >
                      FAILED_TESTS_PREVIEW_COUNT && (
                      <LoadMoreDivider
                        expanded={!!showAllFailures[currentTabKey]}
                        totalCount={test[Object.keys(test)[0]].length}
                        onClick={() => toggleShowAllFailures(currentTabKey)}
                        t={t}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <CustomCard
                  key={test.title}
                  headingText={test.title}
                  subHeadingText={test.subText}
                  loanDetails={loanDetails}
                  fullView={true}
                  ratesAsOfDate={ratesAsOfDate}
                  gridSpace={test?.gridSpace ?? 4}
                  showFailed={true}
                  background={"white"}
                  showRequestWaiver={canViewWaiver}
                  canRequestWaiver={canRequestWaiver}
                  canActOnWaiver={canActOnWaiver}
                  onRequestWaiverClick={onRequestWaiverClick}
                  currentTabResults={tabData}
                  currentTabKey={currentTabKey}
                  testType={(test as any)?.testType}
                  testKey={(test as any)?.testKey}
                  isWaiverFormOpen={
                    openWaiverKey === getWaiverKey(currentTabKey, test.title)
                  }
                  onWaiverSubmit={handleWaiverSubmit}
                  onWaiverCancel={handleWaiverCancel}
                />
              )}
            </Grid>
          ) : (
            <Grid
              item
              xs={gridSize <= 4 ? 5 : gridSize}
              style={{
                borderRight: "1px solid #A9A9A9",
                minWidth: "400px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: "6px 0px 0px 0px",
                  border: "1px solid #B8B8C7",
                  background: "rgba(228, 234, 255, 0.80)",
                  padding: "0px 10px",
                }}
              >
                <h3>{isLoanDSCR ? currentTabKey : test.title}</h3>
                <CustomChip
                  size="small"
                  label={
                    <div className="div-space-between">
                      <img src={InfoIcon} alt="info-icon" className="mr-8" />{" "}
                      {isLoanDSCR
                        ? `Failed - ${test[currentTabKey]?.length}`
                        : "Failed"}
                    </div>
                  }
                  variant="outlined"
                  className={
                    test[currentTabKey]?.length
                      ? "error-chipData"
                      : "success-chipData"
                  }
                  data-testid="custom-chip"
                />
              </div>
              <div>
                {isLoanDSCR ? (
                  <>
                    {(showAllFailures[currentTabKey]
                      ? test[currentTabKey]
                      : test[currentTabKey]?.slice(
                          0,
                          FAILED_TESTS_PREVIEW_COUNT,
                        )
                    )?.map((testEle: ObjectType) => {
                      return (
                        <div key={testEle.title} style={contentDiv}>
                          <CustomCard
                            headingText={testEle.title}
                            subHeadingText={testEle.subText}
                            loanDetails={loanDetails}
                            fullView={true}
                            ratesAsOfDate={ratesAsOfDate}
                            gridSpace={testEle?.gridSpace ?? 4}
                            cardCss={true}
                            showRequestWaiver={canViewWaiver}
                            canRequestWaiver={canRequestWaiver}
                            canActOnWaiver={canActOnWaiver}
                            onRequestWaiverClick={onRequestWaiverClick}
                            currentTabKey={currentTabKey}
                            testType={(testEle as any)?.testType}
                            testKey={(testEle as any)?.testKey}
                            isWaiverFormOpen={
                              openWaiverKey === getWaiverKey(currentTabKey, testEle.title)
                            }
                            onWaiverSubmit={handleWaiverSubmit}
                            onWaiverCancel={handleWaiverCancel}
                          />
                        </div>
                      );
                    })}
                    {test[currentTabKey]?.length >
                      FAILED_TESTS_PREVIEW_COUNT && (
                      <LoadMoreDivider
                        expanded={!!showAllFailures[currentTabKey]}
                        totalCount={test[currentTabKey].length}
                        onClick={() => toggleShowAllFailures(currentTabKey)}
                        t={t}
                      />
                    )}
                  </>
                ) : (
                  <CustomCard
                    key={test.title}
                    headingText={test.title}
                    subHeadingText={test.subText}
                    loanDetails={loanDetails}
                    fullView={true}
                    ratesAsOfDate={ratesAsOfDate}
                    gridSpace={test?.gridSpace ?? 4}
                    showFailed={true}
                    showRequestWaiver={canViewWaiver}
                    canRequestWaiver={canRequestWaiver}
                    canActOnWaiver={canActOnWaiver}
                    onRequestWaiverClick={onRequestWaiverClick}
                    currentTabResults={tabData}
                    currentTabKey={currentTabKey}
                    testType={(test as any)?.testType}
                    testKey={(test as any)?.testKey}
                    isWaiverFormOpen={
                      openWaiverKey === getWaiverKey(currentTabKey, test.title)
                    }
                    onWaiverSubmit={handleWaiverSubmit}
                    onWaiverCancel={handleWaiverCancel}
                  />
                )}
              </div>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
}
